import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { LucideAngularModule } from 'lucide-angular';
import { Subject } from 'rxjs';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { DeleteConfirmationComponent } from '../components/delete-confirmation/delete-confirmation.component';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { ESectionTypes } from '../../shared/models/eop_types.enum';
import { Icons } from '../../shared/helpers/icons';
import { IDistricCoordinatorTraining } from '../../shared/models/schedule';
import { IUserInfo } from '../../shared/models/contact';
import { CalendarOptions, EventInput } from '@fullcalendar/core/index.js';
import { UserDataService } from '../../shared/services/userData.service';
import { FilesService } from '../../shared/services/files.service';
import { DateUtils } from '../../shared/helpers/dateUtils';
import { TrainingDetailsComponent } from './training-details/training-details.component';
import { NewTrainingComponent } from './new-training/new-training.component';
import { EditTrainingComponent } from './edit-training/edit-training.component';
import { TrainingTableComponent } from './training-table/training-table.component';
import { DistrictCoordinatorTrainingService } from '../../shared/services/districtCoordinatorTraining.service';

@Component({
  selector: 'district-coordinator-training',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    PrvNextButtonsComponent,
    EopAssistantComponent,
    FullCalendarModule,
    TrainingDetailsComponent,
    NewTrainingComponent,
    EditTrainingComponent,
    TrainingTableComponent,
    SpinnerComponent,
    PopupMessageComponent,
    DeleteConfirmationComponent,
    IntroductionComponent
  ],
  templateUrl: './district-coordinator-training.component.html',
  styleUrl: './district-coordinator-training.component.css'
})
export class DistrictCoordinatorTrainingComponent implements OnInit {
  
  @ViewChild('fullcalendar') calendarComponent!: FullCalendarComponent;
  private destroy$ = new Subject<void>();

  eopSectionTypeId = ESectionTypes["District Coordinator Training Log"];

  icons = Icons;
  selectedEvent: IDistricCoordinatorTraining | null = null;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  schedulesList: any;
  scheduleTable: IDistricCoordinatorTraining[] = [];
  selectedDate: string | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  schoolYears: string[] = [];
  selectedSchoolYear: string = '';
  deleteItemType: string = '';

  showIntro: boolean = false;
  showEventDetails: boolean = false;
  showEventEdit: boolean = false;
  showDeleteConfirmation: boolean = false;
  showCreateEvent: boolean = false;
  loading: boolean = false;

  calendarOptions: CalendarOptions = {
    plugins: [],
    initialView: 'dayGridMonth',
    events: [] // set it to an empty array initially to prevent hydration issues
  };
  hydrated = false // set it to false initially to prevent hydration issues, only populate it after the schedules are loaded and the calendar options are initialized
   
  constructor(
    private userDataService: UserDataService,
    private districtCoordinatorTrainingService: DistrictCoordinatorTrainingService,
    private filesService: FilesService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.schoolYears = DateUtils.getSchoolYears(6);
    this.selectedSchoolYear = DateUtils.getCurrentSchoolYear();
    this.loadSchedules();   
  }
  
  loadSchedules(): void {
    if (this.currentBuildingId && this.selectedSchoolYear) {
      this.loading = true;
      this.districtCoordinatorTrainingService.getBuildingCoordinatorTrainings(this.currentBuildingId, this.selectedSchoolYear).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Fetched schedules list:', response);
          if (!response.has_error) {
            this.scheduleTable = response.data ? response.data : [];
  
            this.schedulesList = response.data?.map((schedule: IDistricCoordinatorTraining) => ({
              id: schedule.id,
              title: schedule.name,
              start: schedule.start_date,
              end: schedule.end_date,
              extendedProps: {
                time: schedule.time,
                description: schedule.details,
                duration: schedule.duration,
                date_created: schedule.last_updated_at,
                last_updated_user_name: schedule.last_updated_user_name,
                buildingId: schedule.building_id,
                files: schedule.files || []
              }
            }));
  
            // Always call init/refresh directly in CSR
            if (this.hydrated) {
              this.refreshCalendarEvents();
            } else {
              this.hydrated = true;
              this.initializeCalendarOptions(); // first time init
            }
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching schedulesList:', error.message);
        }
      });
    }
  }
  
  
  // This method initializes the calendar options for FullCalendar
  initializeCalendarOptions(): void {
    this.calendarOptions = {
      plugins: [ dayGridPlugin, interactionPlugin ],
      initialView: 'dayGridMonth',
      editable: false, // Disable drag-and-drop
      eventResizableFromStart: false, // Disable resizing events
      dayMaxEventRows: 3,
      events: this.schedulesList,
      dateClick: (arg) => { // when click on a date on the calendar to pop up the create new event form
        if (this.canSave()) {
          this.selectedDate = arg.dateStr; // set the selected date to the clicked date
          this.handleOpenModal('create-event');
        }
      },
      eventClick: (info) => { // when click on an event on the calendar to pop up the details modal
        const eventDetails = {
          id: parseInt(info.event.id, 10),
          start_date: info.event.start,
          end_date: info.event.end ?? info.event.start,
          name: info.event.title,
          details: info.event.extendedProps['description'],
          time: info.event.extendedProps['time'],
          duration: info.event.extendedProps['duration'],
          last_updated_user_name: info.event.extendedProps['last_updated_user_name'],
          building_id: info.event.extendedProps['buildingId'],
          last_updated_at: info.event.extendedProps['date_created'],
          files: info.event.extendedProps['files'] || []
        };
        this.handleOpenModal('event-details', eventDetails);
      },
    };
  }

  // update the calendar options when the schedules are loaded
  updateCalendarOptions(): void {
    if (this.schedulesList && this.schedulesList.length > 0) {
      this.calendarOptions.events = this.schedulesList;
    } else {
      this.calendarOptions.events = []; // set to empty array if no schedules are available
    }
  }

  handleSchoolYearChange(event: string): void {
    this.selectedSchoolYear = event;
    this.loadSchedules(); // Reload schedules when school year changes
  }

  handleOpenModal(modalName: string, selectedItem?: any, itemType?: string): void {
    if (modalName === 'intro') {
      this.showIntro = true;
    } else if (modalName === 'edit-event') {
      this.showEventDetails = false;
      this.showEventEdit = true;
    } else if (modalName === 'create-event') {
      if (!this.selectedDate) { // if no date is selected, default to today and pass it to the modal
        this.selectedDate = new Date().toISOString().split('T')[0];
      }
      this.showCreateEvent = true;
    } else if (modalName === 'event-details') {
      this.showEventDetails = true;
    } else if (modalName === 'delete-confirmation') {
      this.showEventDetails = false;
      this.showDeleteConfirmation = true;
    }
    this.selectedEvent = selectedItem || null;
    this.deleteItemType = itemType || '';
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'intro') {
      this.showIntro = false;
    } else if (modalName === 'edit-event') {
      this.showEventEdit = false;
    } else if (modalName === 'create-event') {
      this.showCreateEvent = false;
    } else if (modalName === 'event-details') {
      this.showEventDetails = false;
    } else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = false;
    }

    this.selectedDate = null; // Reset selected date when closing modals
    this.selectedEvent = null; // Reset selected event when closing modals
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadSchedules();
    this.updateCalendarOptions(); // Update calendar options after saving changes

    if (message.type === 'success') {
      // this.loadAssessmentsCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  handleDelete(item: any): void {
    if (item.id) {
      this.loading = true;
      // handle deletion based on the item type
      // this case is for deleting a calendar event
      if (this.deleteItemType === 'training log') {
        this.districtCoordinatorTrainingService.deleteCoordinatorTraining(String(item.id)).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.messageModal = "success";
              this.popupMessage = 'This occurred after deleting an event.';
              this.handleCloseModal('delete-confirmation');
              this.loadSchedules();
            }
          },
          error: (error) => {
            this.loading = false;
            this.messageModal = "unsuccess";
            this.popupMessage = error.message;
            console.error('Error deleting the meeting:', error.message);
          }
        })
      } else if (this.deleteItemType === 'uploaded file') { // this case is for deleting an uploaded file from an event
        console.log('Deleting file:', item);
        this.filesService.deleteFile(item.building_id, item.section_id, item.id).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.messageModal = "success";
              this.popupMessage = 'This occurred after deleting a file.';
              this.handleCloseModal('delete-confirmation');
              this.loadSchedules();
            }
          },
          error: (error) => {
            this.loading = false;
            this.messageModal = "unsuccess";
            this.popupMessage = error.message;
            console.error('Error deleting the file:', error.message);
          } 
        })
      } 
      this.deleteItemType = '';
    }
  } 

  refreshCalendarEvents() {
    const calendarApi = this.calendarComponent?.getApi();
    if (calendarApi) {
      calendarApi.removeAllEvents(); // clear current events
      this.schedulesList.forEach((event: EventInput) => {
        calendarApi.addEvent(event); // add updated events
      });
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
  
  ngOnDestroy() { // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }


}
